/**
 * Tooltip — minimal, keyboard-accessible hover/focus tooltip primitive
 * (BON-142). Shows `content` in a small floating panel above the trigger on
 * hover OR keyboard focus; hides on mouse-leave / blur / Escape. Purely
 * presentational — callers own the trigger markup via `children` (it should
 * be a single focusable element, e.g. a `<button>`, so keyboard users can
 * reach it via Tab).
 *
 * THE PANEL IS PORTALLED AND POSITIONED IN VIEWPORT COORDINATES, not absolutely
 * inside the wrapper (BON-472). The consumer that forced this is a button in a table
 * whose container scrolls horizontally, and `overflow-x: auto` computes
 * `overflow-y` to `auto` as well — so an absolutely-positioned panel was clipped
 * by the table on the top row and by its right edge on the last column, which is
 * the same dead end as having no tooltip at all.
 *
 * TWO OTHER CONSUMERS EXIST and were not re-verified when that changed — the
 * workshop calendar picker's event pill and its day-overflow popover (operator's
 * accepted risk, 2026-08-25). Both render taller content than the refusal
 * sentence this panel's flip-above threshold is calibrated for, so the
 * "is there room above?" test is optimistic for them, and the portalled panel now
 * paints over the overflow popover instead of under it. Neither has a test.
 * Before changing the geometry again, cover all three.
 */
import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Applied to the wrapping `<span>` — callers matching a block-level
   *  trigger's layout should pass `"block w-full"`. */
  className?: string;
  /** The panel's id, for a trigger that wants to name it in its own
   *  `aria-describedby`. A description on the WRAPPER is not announced: the
   *  wrapper is neither focusable nor an accessible element, so a screen-reader
   *  user hears nothing without this. */
  id?: string;
}

/** Gap between trigger and panel, and the room the panel is assumed to need.
 *  A measurement would need a second render; being generous costs a flip to
 *  below in a case where above would just have fitted. */
const GAP_PX = 6;
const PANEL_HEIGHT_PX = 110;
/** Half of the panel's `w-56`, plus a margin, so it is clamped inside the
 *  viewport rather than running off the edge it is centred against. */
const HALF_PANEL_PX = 112;
const EDGE_PX = 8;

export function Tooltip({ content, children, className = '', id }: TooltipProps) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const trigger = useRef<HTMLSpanElement>(null);
  // Unconditional, then overridden: `id ?? useId()` short-circuits the hook.
  const fallbackId = useId();
  const panelId = id ?? fallbackId;

  const show = () => setAnchor(trigger.current?.getBoundingClientRect() ?? null);
  const hide = () => setAnchor(null);
  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Escape') hide();
  };

  return (
    <span
      ref={trigger}
      className={`relative inline-block ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={onKeyDown}
    >
      {children}
      {anchor &&
        createPortal(
          <span id={panelId} role="tooltip" style={panelStyle(anchor)} className={PANEL_CLASS}>
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}

const PANEL_CLASS =
  'pointer-events-none fixed z-[70] w-56 rounded-lg border border-stone-200 bg-white p-2 text-left text-xs leading-snug text-stone-700 shadow-md dark:border-stone-700 dark:bg-[#1e2520] dark:text-stone-200';

/** Above the trigger unless there is no room, in which case below — a panel
 *  drawn off the top of the viewport is unreadable rather than merely awkward. */
function panelStyle(anchor: DOMRect): CSSProperties {
  const below = anchor.top < PANEL_HEIGHT_PX + GAP_PX;
  const centre = anchor.left + anchor.width / 2;
  const limit = Math.max(HALF_PANEL_PX + EDGE_PX, window.innerWidth - HALF_PANEL_PX - EDGE_PX);
  return {
    left: Math.min(Math.max(centre, HALF_PANEL_PX + EDGE_PX), limit),
    top: below ? anchor.bottom + GAP_PX : anchor.top - GAP_PX,
    transform: below ? 'translateX(-50%)' : 'translate(-50%, -100%)',
  };
}
